import React from "react"
import { computed } from "mobx"
import { observer, useLocalStore } from "mobx-react"
import { Flex, Typography } from "antd"
import Icon from "antd/es/icon"
import { DownOutlined, MinusOutlined, UpOutlined } from "@ant-design/icons"
import classNames from "classnames"

import { getCurrencyIcon } from "@src/shared/getCurrencyIcon"
import {
  MerchantStatsItemDiffType,
  MerchStatsItemType,
} from "@src/widgets/merchantStats/classes/MerchantStats"
import { getChartNameByType } from "@src/widgets/merchantStats/constants"
import css from "./index.module.css"

type Props = {
  block: MerchStatsItemType
}

export const StatsBlock = observer(({ block }: Props) => {
  const title = computed(() => {
    return getChartNameByType(block.type)
  }).get()

  const diff = computed(() => {
    const toNum = Number(block.diff_periods)

    if (toNum > 0) return "positive"

    if (toNum < 0) return "negative"

    return "neutral"
  }).get()

  const formatedDiff = computed(() => {
    const formatNumber = (value: number): string => {
      if (value < 1000) return value.toString()

      const units = [
        { suffix: "k", value: 1e3 },
        { suffix: "M", value: 1e6 },
        { suffix: "B", value: 1e9 },
        { suffix: "T", value: 1e12 },
      ]

      const unit = units
        .slice()
        .reverse()
        .find((u) => value >= u.value)!

      const short = value / unit.value

      return short % 1 === 0
        ? `${short}${unit.suffix}`
        : `${short.toFixed(1)}${unit.suffix}`
    }

    return formatNumber(Number(block.diff_periods))
  }).get()

  const iconByDiffType = computed(() => {
    return (
      {
        negative: <DownOutlined />,
        neutral: <MinusOutlined />,
        positive: <UpOutlined />,
      } as Record<MerchantStatsItemDiffType, React.ReactNode>
    )[diff]
  }).get()

  const classNameByDiffType = computed(() => {
    return (
      {
        negative: css.diffNegative,
        neutral: css.diff,
        positive: css.diffPositive,
      } as Record<MerchantStatsItemDiffType, string>
    )[diff]
  }).get()

  const isNegativeDiff = computed(() => Number(block.diff_periods) < 0).get()

  return (
    <Flex className={css.block} gap={16} vertical>
      <Flex align="center" justify="space-between" style={{ width: "100%" }}>
        <Typography.Title className={css.cardTitle}>{title}</Typography.Title>
      </Flex>

      <Flex vertical>
        <Flex gap={4}>
          <Typography className={css.total}>$</Typography>
          <Typography.Text className={css.total}>{block.total}</Typography.Text>
        </Flex>
        <Flex align="center" gap={6}>
          {iconByDiffType}
          <Typography.Text className={classNameByDiffType}>
            {formatedDiff}$ vs previous period
          </Typography.Text>
        </Flex>
      </Flex>

      <Flex justify="space-between">
        <Flex vertical>
          <Typography.Text className={css.infoTitle}>
            Completed Payments
          </Typography.Text>
          <Typography.Text className={classNames([css.totalCount])}>
            {block.completed_count}
          </Typography.Text>
        </Flex>

        <Flex vertical>
          <Typography.Text className={css.infoTitle}>
            Success Rate
          </Typography.Text>
          <Typography.Text
            className={classNames([
              css.rate,
              { [`${css.rateNegative}`]: isNegativeDiff },
            ])}
          >
            {block.success_rate} %
          </Typography.Text>
        </Flex>
      </Flex>
    </Flex>
  )
})
